import { Footer } from '@/app/components/Footer'
import HOMEPAGE_SEARCH_INDEX_CONFIG from '@/app/config/homepage'
import { getSearchClient } from '@/search-client'
import { useTranslations } from 'next-intl'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { InstantSearch } from 'react-instantsearch'
import { Header } from '../../shared/components/Header'
import { Newsletter } from '../../shared/components/Newsletter'
import { TopProvidersSection } from '../../shared/components/TopProvidersSection'
import { useLanguageSelection } from '../../shared/providers/LanguageSelectionProvider'
import { CoursesHeader } from '../components'
import { GroupedCoursesHits } from '../components/GroupedCoursesHits'
import { SearchInputProvider } from '../contexts/InputSearchContext'

const ITEMS_PER_GROUP = 3
const MAX_GROUPS = 6

export function ExternalCoursesPage() {
  const t = useTranslations('Courses')
  const { currentLocale } = useLanguageSelection()
  const router = useRouter()

  const memoizedSearchClient = useMemo(() => {
    try {
      return getSearchClient({
        server: {
          apiKey: HOMEPAGE_SEARCH_INDEX_CONFIG.apiKey,
        },
        collectionSpecificSearchParameters: {
          subcategories_courses: {
            q: '*',
            group_by: 'subcategory_id',
            group_limit: ITEMS_PER_GROUP,
            include_fields: `$subcategories(name, id, is_promoted, slug) as subcategory,$courses(id,course_id,name_${currentLocale},learning_description_${currentLocale},quality_score,education_provider,instructors,level,min_price) as course`,
            exclude_fields: 'id,course_id,subcategory_id',
            // once we have sufficient promoted categories, we can limit by them and change the sort to randomize them
            // until then, we are limited by the number of sort_by fields TypeSense supports (3)
            sort_by:
              '$subcategories(is_promoted:desc),$courses(quality_score:desc,_rand():desc)',
            limit: MAX_GROUPS,
            query_by: `subcategory_id`,
          },
          /*
          // TODO: searching categories will require another scoped key
          categories: {
            sortBy: 'is_promoted:desc,name:asc',
            query_by: `name`,
            q: '*',
          },
          */
        },
      })
    } catch (e) {
      return null
    }
  }, [currentLocale])

  const onSearchHandler = async (term: string) => {
    await router.push({
      pathname: '/search',
      query: { 'courses[query]': term },
    })
  }

  if (!memoizedSearchClient) {
    return null
  }

  return (
    <>
      <Head>
        <title>{t('title')}</title>
      </Head>
      <SearchInputProvider>
        <Header className="z-40" />
        <InstantSearch
          indexName="subcategories_courses"
          searchClient={memoizedSearchClient}
          future={{
            preserveSharedStateOnUnmount: true,
          }}
          numberLocale={currentLocale}
          stalledSearchDelay={500}
          routing={false}
        >
          <CoursesHeader
            className="md:mx-8"
            onSearch={async (searchTerm) => {
              await onSearchHandler(searchTerm)
            }}
          />
          <div className="container mb-20 mt-12">
            <GroupedCoursesHits
              splitGroupsSize={3}
              className="flex flex-col gap-12"
            >
              {/**
               * Enable as soon as we have pages for the categories
                <CategoriesShowcase />
              */}
            </GroupedCoursesHits>
          </div>
          <TopProvidersSection variant="static" />
          <Newsletter />
          <Footer />
        </InstantSearch>
      </SearchInputProvider>
    </>
  )
}
